import { useApolloClient, useMutation } from '@apollo/react-hooks'
import { useUser } from '@paintscout/ui-business'
import LogRocket from 'logrocket'
import { gql } from 'apollo-boost'
import { useEffect } from 'react'
import { setUserflowVisible } from 'shared/util/userflowVisibility'
import { Sentry } from 'src/util/appSentry'
import {
  HS_CONVERSATIONS_IFRAME_ID,
  guardUserflowWidgetControl,
  hideChatLoader,
  installHubspotConversationsStub,
  installHubspotZIndexCap,
  isCloseActionable,
  isRealHubspotWidget,
  setHsOpenCookie,
  setHubspotVisible,
  showChatLoader,
  watchHubspotContainerHidden
} from 'src/util/hubspotChat'

// Must run at module scope, before Userflow's own mount effect (`UserFlow.tsx`) calls
// `userflow.init()` and its resource center starts probing `window.HubSpotConversations`.
installHubspotConversationsStub()

const HUBSPOT_PORTAL_ID = '341850526'
const HS_POLL_INTERVAL_MS = 100
// The poll for window.HubSpotConversations used to run for the rest of the session with no report,
// so a blocked script or a portal that rejects the hostname left the spinner turning forever and
// told nobody (#22823). Give every wait a deadline that lands in Sentry.
const HS_LOAD_TIMEOUT_MS = 20000
// How long the loader stays behind HubSpot's panel, so there is no flash of empty space.
const HS_LOADER_HANDOFF_MS = 2000
// QA and the ENG-1461 PR both point at this string to confirm the guard fired, so it is named
// rather than inlined. Changing it splits the Sentry issue and breaks that reference.
const BLOCKED_CLOSE_FINGERPRINT = 'hubspot-chat-userflow-close-blocked'

// --- Module-level state (outside React lifecycle so listeners persist) ---

const state = {
  scriptInjected: false,
  hubspotReady: false,
  eventsRegistered: false,
  closeHandler: null as ((e: MessageEvent) => void) | null,
  closeArmedAt: null as number | null,
  armPoll: null as ReturnType<typeof setInterval> | null,
  stopContainerWatch: null as (() => void) | null,
  logRocketUrl: null as string | null,
  getUser: null as (() => any) | null,
  associateSupportTicket: null as ((opts: any) => void) | null,
  apolloClient: null as any,
  cachedVisitorToken: null as string | null,
  blockedCloseReported: false
}

function reportChatFailure(error: Error, extra?: Record<string, any>) {
  Sentry.captureException(error, {
    tags: { feature: 'hubspot-chat', stage: process.env.DEPLOYMENT_STAGE },
    extra: { portalId: HUBSPOT_PORTAL_ID, ...extra }
  })
}

/**
 * Takes `widget.close()` away from Userflow for as long as the panel is still opening.
 *
 * Idempotent per widget object, so it is safe to call on every open and again once the panel
 * renders. `hs.widget.load()` can hand back a fresh `widget`, and the guard marker stays with the
 * old one — without the second call the first open of a session runs unguarded (ENG-1461).
 */
function guardWidget(widget?: NonNullable<Window['HubSpotConversations']>['widget']) {
  const guarded = guardUserflowWidgetControl(widget, {
    allowClose: () => isCloseActionable(state.closeArmedAt, Date.now()),
    onBlockedClose: () => {
      if (state.blockedCloseReported) {
        return
      }
      // One report per open. A Userflow build that retries its `hide()` would otherwise flood the
      // project. This message appearing is the confirmation that Userflow is the thing closing the
      // panel — before this guard existed the close looked legitimate and reached Sentry as nothing.
      state.blockedCloseReported = true
      Sentry.captureMessage('Blocked a third-party widget.close() while the chat panel was opening', {
        level: 'warning',
        fingerprint: [BLOCKED_CLOSE_FINGERPRINT],
        tags: { feature: 'hubspot-chat', stage: process.env.DEPLOYMENT_STAGE }
      })
    }
  })
  if (guarded?.length === 0) {
    reportChatFailure(new Error('HubSpot widget exposes none of hide, show or close'), {
      step: 'guardUserflowWidgetControl'
    })
  }
}

// --- Close detection ---

/**
 * Hides the widget and hands the bottom-right corner back to Userflow.
 *
 * Every close signal funnels through here so one guard covers all of them. `isCloseActionable`
 * drops anything that arrives before the panel is on screen, which is what made the chat close
 * itself right after opening (#22823).
 */
function handleWidgetClosed() {
  if (!isCloseActionable(state.closeArmedAt, Date.now())) {
    // Do not clear closeArmedAt here. HubSpot's boot `open-change` lands inside the grace window on
    // every open, and clearing the arm made every later close unactionable — so the corner was
    // never handed back and the Userflow launcher stayed hidden for the session (ENG-1461). The
    // stale-arm case that line guarded is handled by the open handler, which clears the arm before
    // it opens anything.
    return
  }
  state.closeArmedAt = null
  if (state.closeHandler) {
    window.removeEventListener('message', state.closeHandler)
    state.closeHandler = null
  }
  // Disconnect before hiding, so our own teardown does not trip the third-party-hide watcher.
  // `closeArmedAt` is already null by this point, and JavaScript runs this function to completion,
  // so a second close signal arriving mid-teardown returns at the guard above instead of re-running
  // this block. The read-then-null pair does not need to be atomic.
  state.stopContainerWatch?.()
  state.stopContainerWatch = null
  setHsOpenCookie(false)
  setHubspotVisible(false)
  setUserflowVisible(true, { reopenResourceCenter: false })
}

/**
 * Fallback close signal: HubSpot's iframe posts its own `open-change` messages. Kept in case
 * `widgetClosed` is missing from the loaded script version, but it now shares the guard above, so
 * the `open-change` traffic HubSpot emits while booting can no longer hide the panel.
 */
function startCloseDetection() {
  if (state.closeHandler) {
    window.removeEventListener('message', state.closeHandler)
  }

  const handler = (event: MessageEvent) => {
    if (typeof event.data !== 'string') {
      return
    }
    try {
      const msg = JSON.parse(event.data)
      if (msg.type !== 'open-change' || msg.data?.isOpen !== false) {
        return
      }
      handleWidgetClosed()
    } catch {
      // not JSON, ignore
    }
  }

  state.closeHandler = handler
  window.addEventListener('message', handler)
}

/**
 * Backstop for a third party hiding the panel we just opened.
 *
 * `neutralizeUserflowWidgetControl` takes `widget.hide()` away from Userflow, but the container can
 * still be hidden by a route we have not seen. Without this, the user is left with no chat panel and
 * no Userflow launcher, which is the state QA reported on Test (ENG-1461). Hand the corner back and
 * report it, so a new hide route shows up in Sentry instead of only in a QA comment.
 */
function watchForThirdPartyHide() {
  state.stopContainerWatch?.()
  state.stopContainerWatch = watchHubspotContainerHidden(() => {
    state.stopContainerWatch = null
    state.closeArmedAt = null
    // Report before restoring, so a throw inside `setUserflowVisible` cannot lose the report.
    reportChatFailure(new Error('HubSpot chat container hidden by a third party'))
    setUserflowVisible(true, { reopenResourceCenter: false })
  })
}

/**
 * Arms close detection once HubSpot's panel iframe exists. That iframe is the only signal we have
 * that the panel really rendered, so it is what separates a real close from a boot artifact.
 *
 * Only one poll runs at a time. Repeated `ps:open-hubspot-chat` events used to start a poll each,
 * so a slow load reported the same timeout to Sentry once per click.
 */
function armCloseDetectionWhenPanelRenders() {
  if (state.armPoll) {
    clearInterval(state.armPoll)
  }
  const startedAt = Date.now()
  const poll = setInterval(() => {
    if (document.getElementById(HS_CONVERSATIONS_IFRAME_ID)) {
      clearInterval(poll)
      state.armPoll = null
      state.closeArmedAt = Date.now()
      // `widget.load()` may have replaced the widget object since the open path guarded it.
      guardWidget(window.HubSpotConversations?.widget)
      setTimeout(hideChatLoader, HS_LOADER_HANDOFF_MS)
      watchForThirdPartyHide()
      return
    }
    if (Date.now() - startedAt >= HS_LOAD_TIMEOUT_MS) {
      clearInterval(poll)
      state.armPoll = null
      hideChatLoader()
      setUserflowVisible(true, { reopenResourceCenter: false })
      reportChatFailure(new Error('HubSpot chat panel iframe never rendered'), {
        timeoutMs: HS_LOAD_TIMEOUT_MS
      })
    }
  }, HS_POLL_INTERVAL_MS)
  state.armPoll = poll
}

// --- Open request handler (registered once at module level) ---

const HUBSPOT_VISITOR_TOKEN_QUERY = gql`
  query hubspotVisitorToken {
    hubspotVisitorToken
  }
`

async function fetchVisitorToken() {
  if (state.cachedVisitorToken || !state.apolloClient) {
    return
  }
  try {
    const { data } = await state.apolloClient.query({
      query: HUBSPOT_VISITOR_TOKEN_QUERY,
      fetchPolicy: 'network-only'
    })
    state.cachedVisitorToken = data?.hubspotVisitorToken || null
    if (!state.cachedVisitorToken) {
      // The chat still opens, but HubSpot treats the visitor as anonymous, so support loses the
      // account context. Fingerprinted so a portal-wide outage collapses into one issue. The
      // fingerprint differs from the resolver's so the two stay separable in Sentry: this one
      // means the query answered with null, the resolver's means HubSpot answered without a token.
      Sentry.captureMessage('hubspotVisitorToken returned no token', {
        level: 'warning',
        fingerprint: ['hubspot-visitor-token-empty-client'],
        tags: { feature: 'hubspot-chat', stage: process.env.DEPLOYMENT_STAGE }
      })
    }
  } catch (error) {
    // Chat can still open without identification, so carry on — but say so.
    reportChatFailure(error as Error, { step: 'fetchVisitorToken' })
  }
}

function registerHubspotEvents(hs: any) {
  if (state.eventsRegistered) {
    return
  }

  hs.on('conversationStarted', ({ conversation }: any) => {
    const conversationId = conversation.conversationId
    state.associateSupportTicket?.({
      variables: { conversationId: conversationId.toString(), logRocketUrl: state.logRocketUrl }
    })
  })

  // HubSpot's own close event. Preferred over the postMessage fallback because it fires for a real
  // close only, not for the open-change traffic the iframe emits while it boots.
  try {
    hs.on('widgetClosed', handleWidgetClosed)
  } catch (error) {
    // A script version without this event leaves the postMessage fallback in charge. Report it
    // once so we know the fallback is the only thing holding close detection up.
    reportChatFailure(error as Error, { step: 'registerWidgetClosed' })
  }

  // Set last. Setting it first meant a throw above locked the flag on for the session, so
  // `widgetClosed` and `conversationStarted` never registered on a later click (ENG-1461).
  state.eventsRegistered = true
}

window.addEventListener('ps:open-hubspot-chat', async () => {
  setUserflowVisible(false)
  installHubspotZIndexCap()
  // Clear any arm left over from a previous open. `armCloseDetectionWhenPanelRenders` sets a fresh
  // one when the panel renders, so a real close cannot be judged against a stale timestamp.
  state.closeArmedAt = null
  state.blockedCloseReported = false

  // If HubSpot widget is already loaded, just show and open it. `isRealHubspotWidget` rejects the
  // module-scope stub, which is truthy from page load and has no `on`.
  const loaded = window.HubSpotConversations
  if (state.hubspotReady && isRealHubspotWidget(loaded)) {
    // Idempotent. Re-run in case HubSpot's script replaced the widget object since the first open.
    guardWidget(loaded.widget)
    setHsOpenCookie(true)
    setHubspotVisible(true)
    loaded.widget.open()
    armCloseDetectionWhenPanelRenders()
    startCloseDetection()
    return
  }

  // Show loading placeholder while HubSpot loads
  showChatLoader()

  // Fetch visitor token, then inject HubSpot script
  await fetchVisitorToken()

  // Set identification before loading the widget
  const user = state.getUser?.()
  if (user) {
    const settings: Record<string, string> = {
      identificationEmail: user.email
    }
    if (state.cachedVisitorToken) {
      settings.identificationToken = state.cachedVisitorToken
    }
    ;(window as any).hsConversationsSettings = {
      loadImmediately: false,
      ...settings
    }
  } else {
    ;(window as any).hsConversationsSettings = { loadImmediately: false }
  }

  // Inject HubSpot script if not already done
  if (!state.scriptInjected) {
    state.scriptInjected = true
    const script = document.createElement('script')
    script.src = `//js-na3.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`
    script.async = true
    script.onerror = () => {
      hideChatLoader()
      setUserflowVisible(true, { reopenResourceCenter: false })
      reportChatFailure(new Error('HubSpot chat script failed to load'), { src: script.src })
    }
    document.head.appendChild(script)
  }

  // Poll for HubSpotConversations, then load and open the widget.
  //
  // The poll waits for `widget`, not just `HubSpotConversations`. HubSpot's script defines the
  // namespace first and fills in `widget` a moment later, so the old `if (!hs) return` could clear
  // the interval inside that gap and then throw on `hs.widget.status()`. The throw happened inside
  // a setInterval callback that was already cleared, so the open silently did nothing and
  // `state.hubspotReady` stayed false — the user had to click a second time (#22823). Userflow's
  // own HubSpot integration hits the same gap: PostHog console logs on Test show
  // "Userflow.js: Error when hiding HUBSPOT: TypeError: Cannot read properties of undefined
  // (reading 'widget')".
  const startedAt = Date.now()
  const interval = setInterval(() => {
    const hs = window.HubSpotConversations
    if (!isRealHubspotWidget(hs)) {
      if (Date.now() - startedAt < HS_LOAD_TIMEOUT_MS) {
        return
      }
      clearInterval(interval)
      hideChatLoader()
      setUserflowVisible(true, { reopenResourceCenter: false })
      reportChatFailure(new Error('HubSpotConversations.widget never became available'), {
        timeoutMs: HS_LOAD_TIMEOUT_MS,
        namespaceDefined: Boolean(hs)
      })
      return
    }
    clearInterval(interval)

    try {
      // Take `widget.close()` away from Userflow before anything is opened, so its resource-center
      // integration cannot close the panel we are about to show.
      guardWidget(hs.widget)
      registerHubspotEvents(hs)
      setHsOpenCookie(true)
      // An earlier close left `display: none !important` on the container. Clear it, or this open
      // runs against a hidden panel.
      setHubspotVisible(true)

      // If widget is already loaded, just open it
      if (hs.widget.status().loaded) {
        hideChatLoader()
        hs.widget.open()
        state.hubspotReady = true
        armCloseDetectionWhenPanelRenders()
        startCloseDetection()
        return
      }

      // Load and open the widget
      hs.widget.load()
      hs.widget.open()
      state.hubspotReady = true

      armCloseDetectionWhenPanelRenders()
      startCloseDetection()
    } catch (error) {
      // Anything thrown here used to leave the spinner up and hubspotReady false, with no report.
      hideChatLoader()
      setUserflowVisible(true, { reopenResourceCenter: false })
      reportChatFailure(error as Error, { step: 'openWidget' })
    }
  }, HS_POLL_INTERVAL_MS)
})

// --- React component (wires up dynamic values to module state) ---

export default function HubspotChat() {
  const { user } = useUser()
  const apolloClient = useApolloClient()
  const [associateSupportTicket] = useMutation(gql`
    mutation associateSupportTicket($conversationId: String!, $logRocketUrl: String) {
      associateSupportTicket(conversationId: $conversationId, logRocketUrl: $logRocketUrl)
    }
  `)

  state.getUser = () => user
  state.associateSupportTicket = associateSupportTicket
  state.apolloClient = apolloClient

  useEffect(() => {
    try {
      LogRocket.getSessionURL((sessionURL) => {
        state.logRocketUrl = sessionURL
      })
    } catch {
      // LogRocket not available
    }
  }, [])

  return null
}
